import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SimpleMDE from 'react-simplemde-editor';
import { articlesAPI, publishAPI } from '../services/api';
import Header from '../components/Header';
import './PublishArticle.css';

const PublishArticle = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [article, setArticle] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [results, setResults] = useState(null);
  const [linkedinToken, setLinkedinToken] = useState('');

  useEffect(() => {
    loadArticle();
  }, [id]);

  const loadArticle = async () => {
    try {
      setLoading(true);
      const response = await articlesAPI.getById(id);
      setArticle(response.data.article);
      setEditedContent(response.data.article.article_content);

      // Pre-select platforms that are still pending
      const pending = [];
      ['substack', 'medium', 'linkedin'].forEach((platform) => {
        if (response.data.article.platforms?.[platform]?.status !== 'published') {
          pending.push(platform);
        }
      });
      setSelectedPlatforms(pending);
    } catch (error) {
      console.error('Failed to load article:', error);
      alert('Failed to load article');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handlePlatformToggle = (platform) => {
    if (selectedPlatforms.includes(platform)) {
      setSelectedPlatforms(selectedPlatforms.filter((p) => p !== platform));
    } else {
      setSelectedPlatforms([...selectedPlatforms, platform]);
    }
  };

  const isPlatformPublished = (platform) => {
    return article?.platforms?.[platform]?.status === 'published';
  };

  const getPlatformUrl = (platform) => {
    return article?.platforms?.[platform]?.url;
  };

  const handlePublish = async () => {
    if (selectedPlatforms.length === 0) {
      alert('Please select at least one platform');
      return;
    }

    // Check if LinkedIn is selected but no token
    if (selectedPlatforms.includes('linkedin') && !linkedinToken) {
      alert('LinkedIn publishing requires authentication. Please connect to LinkedIn first.');
      return;
    }

    try {
      setPublishing(true);
      const response = await publishAPI.publishArticle(
        id,
        selectedPlatforms,
        editedContent,
        linkedinToken || undefined
      );

      setResults(response.data.results);

      // Reload article to get updated statuses
      await loadArticle();
    } catch (error) {
      console.error('Publishing failed:', error);
      alert(error.response?.data?.message || 'Publishing failed');
    } finally {
      setPublishing(false);
    }
  };

  const handleLinkedInConnect = async () => {
    try {
      const response = await publishAPI.getLinkedInAuthUrl();
      const authUrl = response.data.authUrl;

      // Open LinkedIn OAuth in popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        'LinkedIn OAuth',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Listen for OAuth callback (you'd need to implement a callback handler)
      const checkPopup = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(checkPopup);
          }
          // This is simplified - in production, you'd handle the OAuth callback properly
        } catch (error) {
          // Cross-origin error is expected
        }
      }, 500);
    } catch (error) {
      console.error('LinkedIn auth error:', error);
      alert('Failed to connect to LinkedIn');
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <Header />
        <div className="container">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading article...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <Header />

      <div className="container publish-container">
        <div className="publish-header">
          <div>
            <h2>Publish Article</h2>
            <p className="article-title-display">{article.article_title}</p>
          </div>
          <button className="btn btn-secondary" onClick={() => navigate('/')}>
            Back to Dashboard
          </button>
        </div>

        <div className="publish-grid">
          {/* Left: Content Editor */}
          <div className="publish-editor-section">
            <div className="section-card">
              <h3>Edit Content Before Publishing</h3>
              <p className="section-description">
                You can make final edits to the article content before publishing. Changes will not
                affect the saved article.
              </p>

              <SimpleMDE
                value={editedContent}
                onChange={setEditedContent}
                options={{
                  spellChecker: false,
                  toolbar: ['bold', 'italic', 'heading', '|', 'preview'],
                  minHeight: '400px',
                }}
              />
            </div>
          </div>

          {/* Right: Platform Selection */}
          <div className="publish-platforms-section">
            <div className="section-card">
              <h3>Select Platforms</h3>
              <p className="section-description">Choose where to publish this article</p>

              <div className="platforms-list">
                {/* Substack */}
                <div className="platform-option">
                  <label className="platform-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedPlatforms.includes('substack')}
                      onChange={() => handlePlatformToggle('substack')}
                      disabled={isPlatformPublished('substack')}
                    />
                    <div className="platform-info">
                      <div className="platform-header">
                        <span className="platform-name">Substack</span>
                        {isPlatformPublished('substack') && (
                          <span className="badge badge-success">Published</span>
                        )}
                      </div>
                      {getPlatformUrl('substack') && (
                        <a
                          href={getPlatformUrl('substack')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="platform-url"
                        >
                          View published article →
                        </a>
                      )}
                    </div>
                  </label>
                </div>

                {/* Medium */}
                <div className="platform-option">
                  <label className="platform-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedPlatforms.includes('medium')}
                      onChange={() => handlePlatformToggle('medium')}
                      disabled={isPlatformPublished('medium')}
                    />
                    <div className="platform-info">
                      <div className="platform-header">
                        <span className="platform-name">Medium</span>
                        {isPlatformPublished('medium') && (
                          <span className="badge badge-success">Published</span>
                        )}
                      </div>
                      {getPlatformUrl('medium') && (
                        <a
                          href={getPlatformUrl('medium')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="platform-url"
                        >
                          View published article →
                        </a>
                      )}
                    </div>
                  </label>
                </div>

                {/* LinkedIn */}
                <div className="platform-option">
                  <label className="platform-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedPlatforms.includes('linkedin')}
                      onChange={() => handlePlatformToggle('linkedin')}
                      disabled={isPlatformPublished('linkedin')}
                    />
                    <div className="platform-info">
                      <div className="platform-header">
                        <span className="platform-name">LinkedIn</span>
                        {isPlatformPublished('linkedin') && (
                          <span className="badge badge-success">Published</span>
                        )}
                      </div>
                      {getPlatformUrl('linkedin') ? (
                        <a
                          href={getPlatformUrl('linkedin')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="platform-url"
                        >
                          View published article →
                        </a>
                      ) : (
                        !linkedinToken && (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={handleLinkedInConnect}
                          >
                            Connect LinkedIn
                          </button>
                        )
                      )}
                    </div>
                  </label>
                </div>
              </div>

              {/* Publish Button */}
              <button
                className="btn btn-primary btn-block btn-lg"
                onClick={handlePublish}
                disabled={publishing || selectedPlatforms.length === 0}
              >
                {publishing
                  ? 'Publishing...'
                  : `Publish to ${selectedPlatforms.length} Platform${selectedPlatforms.length !== 1 ? 's' : ''}`}
              </button>
            </div>

            {/* Results */}
            {results && (
              <div className="section-card results-section">
                <h3>Publishing Results</h3>
                <div className="results-list">
                  {results.map((result, idx) => (
                    <div
                      key={idx}
                      className={`result-item ${result.success ? 'success' : 'error'}`}
                    >
                      <div className="result-platform">{result.platform}</div>
                      <div className="result-message">
                        {result.success ? (
                          <>
                            ✓ {result.message}
                            {result.url && (
                              <a
                                href={result.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="result-url"
                              >
                                View →
                              </a>
                            )}
                          </>
                        ) : (
                          <>✗ {result.error || 'Failed to publish'}</>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublishArticle;
